import { MockStreamSpeed } from "../types";

type StreamProfile = {
  initialDelayMs: number;
  chunkDelayRangeMs: [number, number];
  chunkSizeRange: [number, number];
};

const STREAM_PROFILES: Record<MockStreamSpeed, StreamProfile> = {
  slow: {
    initialDelayMs: 400,
    chunkDelayRangeMs: [85, 125],
    chunkSizeRange: [2, 6],
  },
  normal: {
    initialDelayMs: 250,
    chunkDelayRangeMs: [40, 70],
    chunkSizeRange: [3, 12],
  },
  fast: {
    initialDelayMs: 120,
    chunkDelayRangeMs: [20, 35],
    chunkSizeRange: [8, 18],
  },
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

const createSeededRandom = (seedText: string) => {
  let seed = 2166136261;
  for (let i = 0; i < seedText.length; i += 1) {
    seed ^= seedText.charCodeAt(i);
    seed = Math.imul(seed, 16777619);
  }

  return () => {
    seed = Math.imul(seed + 0x6d2b79f5, 1);
    seed ^= seed >>> 15;
    seed = Math.imul(seed, 1 | seed);
    seed ^= seed + Math.imul(seed ^ (seed >>> 7), 61 | seed);
    return ((seed ^ (seed >>> 14)) >>> 0) / 4294967296;
  };
};

const getRandomInRange = (
  random: () => number,
  [min, max]: [number, number]
): number => {
  return Math.floor(random() * (max - min + 1)) + min;
};

export const streamMockTextChunks = async function* (
  fullText: string,
  options?: {
    speed?: MockStreamSpeed;
    seed?: string;
  }
): AsyncGenerator<string, void, void> {
  const profile = STREAM_PROFILES[options?.speed || "normal"];
  const random = createSeededRandom(options?.seed || fullText);

  await sleep(profile.initialDelayMs);

  let cursor = 0;
  while (cursor < fullText.length) {
    const chunkSize = getRandomInRange(random, profile.chunkSizeRange);
    cursor = Math.min(cursor + chunkSize, fullText.length);
    yield fullText.slice(0, cursor);

    if (cursor < fullText.length) {
      const delayMs = getRandomInRange(random, profile.chunkDelayRangeMs);
      await sleep(delayMs);
    }
  }
};

export const streamMockText = async (
  fullText: string,
  options?: {
    speed?: MockStreamSpeed;
    seed?: string;
    onChunk?: (text: string) => void;
  }
): Promise<void> => {
  for await (const chunk of streamMockTextChunks(fullText, options)) {
    options?.onChunk?.(chunk);
  }
};
